import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import authAxios from 'src/modules/shared/axios/authAxios';

export default class ResourceService {
  static async previewDocumentName(pattern: string) {
    const response = await authAxios.post(`/tenant/${AuthCurrentTenant.get()}/document-name-preview`, { pattern });
    return response.data;
  }

  static async downloadNamedPdf(id: string) {
    const response = await authAxios.get(`/tenant/${AuthCurrentTenant.get()}/resource/${id}/named-pdf`, { responseType: 'blob' });
    return response;
  }
  static async update(id, data) {
    const body = {
      id,
      data,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/resource/${id}`,
      body,
    );

    return response.data;
  }

  static syncDatabases(data) {
    const body = {
      data,
    };

    const tenantId = AuthCurrentTenant.get();

    return authAxios.post(
      `/tenant/${tenantId}/resource/sync-databases`,
      body,
    );
  }

  // Re-sincroniza UN documento por el MISMO flujo que el botón por fechas
  // (syncPartialDatabases → syncResourceFromFirestoreToDb), scoped a un id.
  // El backend exige un array no vacío: { $in: [id] }. Nunca enviar [].
  static resyncDocument(id) {
    if (!id) {
      throw new Error('resyncDocument requires a document id');
    }

    return ResourceService.syncDatabases({
      partial: true,
      ids: [id],
    });
  }

  // Dry-run de solo lectura: NO ejecuta el sync. Devuelve cuántos documentos
  // coincidirían con los filtros y una muestra de sus ids, para que el usuario
  // confirme el alcance ANTES de ejecutar. Acepta { formTemplate, status,
  // dateFrom, dateTo } (filtros) o { ids } (multiselección).
  static async previewSync(filters) {
    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.post(
      `/tenant/${tenantId}/resource/sync-databases`,
      { data: { ...filters, partial: true, preview: true } },
    );

    // { count, ids, truncated }
    return response.data;
  }

  // Re-sincroniza SOLO los documentos seleccionados (mismo flujo, scoped a ids).
  static resyncSelected(ids) {
    if (!Array.isArray(ids) || !ids.length) {
      throw new Error('resyncSelected requires a non-empty id array');
    }

    return ResourceService.syncDatabases({
      partial: true,
      ids,
    });
  }

  static async destroyAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.delete(
      `/tenant/${tenantId}/resource`,
      {
        params,
      },
    );

    return response.data;
  }

  static async create(data) {
    const body = {
      data,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.post(
      `/tenant/${tenantId}/resource`,
      body,
    );

    return response.data;
  }

  static async import(values, importHash) {
    const body = {
      data: values,
      importHash,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.post(
      `/tenant/${tenantId}/resource/import`,
      body,
    );

    return response.data;
  }

  static async find(
    id,
    database: string | null = null,
    publicAccess = false,
  ) {
    const tenantId = publicAccess
      ? null
      : AuthCurrentTenant.get();

    const response = await authAxios.get(
      tenantId
        ? `/tenant/${tenantId}/resource/${id}`
        : `/tenant/resource/${id}`,
      database
        ? {
            headers: { database },
          }
        : undefined,
    );

    return response.data;
  }

  static async findPublic(
    id,
    database: string | null = null,
  ) {
    return ResourceService.find(id, database, true);
  }

  static async list(filter, orderBy, limit, offset) {
    const params = {
      filter,
      orderBy,
      limit,
      offset,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.get(
      `/tenant/${tenantId}/resource`,
      {
        params,
      },
    );

    return response.data;
  }

  static async listAutocomplete(query, limit) {
    const params = {
      query,
      limit,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.get(
      `/tenant/${tenantId}/resource/autocomplete`,
      {
        params,
      },
    );

    return response.data;
  }

  static syncMissingFromBigQuery() {
    const tenantId = AuthCurrentTenant.get();

    return authAxios.post(
      `/tenant/${tenantId}/resource/sync-bigquery-missing`,
      {},
    );
  }

  static async destroyAllWithFirestore(ids) {
    const tenantId = AuthCurrentTenant.get();

    return authAxios.post(
      `/tenant/${tenantId}/resource/destroy-firestore`,
      {
        ids,
      },
    );
  }

  static async treeFocus(focusId: string | null) {
    const tenantId = AuthCurrentTenant.get();
    const params = focusId ? { focusId } : {};

    const response = await authAxios.get(
      `/tenant/${tenantId}/resource/tree`,
      { params },
    );

    return response.data;
  }

  static async acquireLock(
    id: string,
    force: boolean = false,
  ) {
    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.post(
      `/tenant/${tenantId}/resource/${id}/lock`,
      { force },
    );

    return response.data;
  }

  static async releaseLock(id: string) {
    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.delete(
      `/tenant/${tenantId}/resource/${id}/lock`,
    );

    return response.data;
  }
}
